import { formatAed } from '../finance/Money';

/**
 * Spec 085 — display and input rules for the admin dealer screens. Money comes from the API as 2-dp
 * strings and is only formatted here, never computed; the input checks mirror the backend's
 * (`DealerCreditMoney`) so an admin sees the problem before the 422.
 */

const DATE = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Dubai', day: 'numeric', month: 'short', year: 'numeric' });
const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Dubai', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

/** "9500.00" → "AED 9,500.00"; absent → em dash. */
export function aed(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? formatAed(n) : value;
}

/** "12.50" → "12.5%". */
export function pct(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? `${n}%` : `${value}%`;
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  // A bare LocalDate ("2026-11-03") is a calendar day: format it without a timezone shift.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00Z`) : new Date(value);
  return Number.isNaN(d.getTime()) ? value : DATE.format(d);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : DATE_TIME.format(d);
}

/** Today in Asia/Dubai as `YYYY-MM-DD` (the default value date). */
export function todayDubai(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai' }).format(new Date());
}

/** "AWAITING_PAYMENT" → "Awaiting payment". */
export function humanize(value: string | null | undefined): string {
  if (!value) return '—';
  const s = value.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const AMOUNT = /^\d+(\.\d{1,2})?$/;

/** A positive AED amount with at most 2 decimals; null when fine. */
export function amountError(raw: string, opts: { allowZero?: boolean } = {}): string | null {
  const t = raw.trim();
  if (t === '') return 'Enter an amount';
  if (!AMOUNT.test(t)) return 'Enter an AED amount with at most 2 decimals';
  if (!opts.allowZero && Number(t) <= 0) return 'Must be more than 0';
  return null;
}

/** BR-9 — a whole AED face value inside the range. */
export function wholeAedError(raw: string, min: number, max: number): string | null {
  const t = raw.trim();
  if (t === '') return 'Enter an amount';
  if (!/^\d+$/.test(t)) return 'Whole AED only (no decimals)';
  const n = Number(t);
  if (n < min || n > max) return `Must be AED ${min.toLocaleString('en-AE')}–${max.toLocaleString('en-AE')}`;
  return null;
}

/** `DealerCreditMoney.requireDiscountPct`: 0 ≤ x < 100, at most 2 decimals. */
export function discountError(raw: string): string | null {
  const t = raw.trim();
  if (t === '') return 'Enter a discount (0 for none)';
  if (!AMOUNT.test(t)) return 'Use a number with at most 2 decimals';
  if (Number(t) >= 100) return 'Must be between 0 and 99.99%';
  return null;
}

/** Same values as the backend compares them ("9500" = "9500.00"). */
export function sameAmount(a: string, b: string): boolean {
  const x = Number(a.trim());
  const y = Number(b.trim());
  return a.trim() !== '' && b.trim() !== '' && Number.isFinite(x) && Number.isFinite(y) && x === y;
}
